import { usePageTitle } from "@/hooks/usePageTitle";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  usePageTitle("Payment");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) { setState("failed"); return; }

    const poll = async (attempt = 0) => {
      if (attempt >= 6) { setState("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F4] px-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E7E5E4] shadow-sm p-10 text-center" data-testid="payment-return">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-teal animate-spin mx-auto" />
            <h2 className="mt-6 font-heading text-2xl text-navy">Confirming your payment…</h2>
            <p className="mt-2 text-sm text-[#57534E]">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-teal mx-auto" />
            <h2 className="mt-6 font-heading text-2xl text-navy">You're in! 🎉</h2>
            <p className="mt-2 text-sm text-[#57534E]">Your {info?.plan_name} (£{info?.amount}) is now active.</p>
            <button onClick={() => navigate("/portal")} data-testid="payment-go-portal"
              className="mt-7 w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-dark transition-colors">Go to member portal</button>
            <Link to="/" className="mt-3 block text-sm text-teal">Back to home</Link>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h2 className="mt-6 font-heading text-2xl text-navy">{state === "timeout" ? "Still processing" : "Payment not completed"}</h2>
            <p className="mt-2 text-sm text-[#57534E]">{state === "timeout" ? "Check your email for confirmation, or try refreshing." : "Your payment was cancelled or failed."}</p>
            <button onClick={() => navigate("/join")} className="mt-7 w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-dark transition-colors">Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
